import { Fragment, useState, type ReactNode } from 'react';
import { fill, formatDate, t, type BomCostChangeAsk, type BomCostChangeRecord, type BomCostDocument, type QuoteBomChange } from '@bms2/shared';
import { Money } from '../../components/Money';
import { TextInput } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import pageStyles from './RecordPage.module.css';
import styles from './BomCostChange.module.css';

/**
 * A bill of materials whose cost moved after a Quote drawn from it went out
 * (piece 18; decision 1466): the confirmation a costed save opens, naming
 * every document the save will make, and the warning line the list's page,
 * the Deal's tile and the Quote's page draw for the newest such change.
 */

/** A figure in dollars drawn through the Money component (decision 0717). */
const figure = (cents: number): ReactNode => <Money cents={cents} />;

/** Fills a string's `{name}` places with nodes, so a figure inside a sentence stays the Money component. */
function filledNodes(template: string, values: Record<string, ReactNode>): ReactNode {
  return template.split(/(\{\w+\})/).map((part, i) => {
    const name = /^\{(\w+)\}$/.exec(part)?.[1];
    return <Fragment key={i}>{name !== undefined && name in values ? values[name] : part}</Fragment>;
  });
}

/** One document the save owes, in the words the confirmation lists it. */
export function documentLine(doc: BomCostDocument): string {
  if (doc.kind === 'quoteRevision') return fill(t('deal.bom.costChange.quoteRevision'), { deal: doc.dealName, opens: doc.opens, standing: doc.standing });
  return fill(t('deal.bom.costChange.refused'), { deal: doc.dealName, sentence: doc.sentence });
}

/** Whether a refusal's answer is the costed-change confirmation, and its ask where it is. */
export function costChangeAskOf(body: unknown): BomCostChangeAsk | null {
  if (body === null || typeof body !== 'object') return null;
  const ask = (body as { costChange?: unknown }).costChange;
  if (ask === null || typeof ask !== 'object' || !Array.isArray((ask as BomCostChangeAsk).documents)) return null;
  return ask as BomCostChangeAsk;
}

/**
 * 🔒 The confirmation (decision 1466): the cost before and after, every
 * document the save will make, and Yes. A save owing a document it cannot make
 * offers no Yes. A holder of the override is offered the reason instead, and
 * typing one turns the Primary into the save without the document.
 */
export function BomCostChangeModal({ ask, saving, onConfirm, onOverride, onCancel }: { ask: BomCostChangeAsk; saving: boolean; onConfirm: () => void; onOverride: (reason: string) => void; onCancel: () => void }) {
  const [reason, setReason] = useState('');
  const typed = reason.trim();
  const refused = ask.documents.some((doc) => doc.kind === 'refused');
  const overriding = ask.canOverride && typed !== '';
  return (
    <Modal
      open
      title={t('deal.bom.costChange.title')}
      onCancel={onCancel}
      dataModal="bom-cost-change"
      actions={[
        { kind: 'primary', label: overriding ? t('deal.bom.costChange.override') : t('deal.bom.costChange.confirm'), onAct: () => (overriding ? onOverride(typed) : onConfirm()), disabled: saving || (refused && !overriding) },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel, disabled: saving },
      ]}
    >
      <p className={styles.intro}>{t('deal.bom.costChange.intro')}</p>
      {ask.costBeforeCents !== null && ask.costAfterCents !== null ? <p className={styles.cost} data-bom-cost-figures="true">{filledNodes(t('deal.bom.costChange.cost'), { before: figure(ask.costBeforeCents), after: figure(ask.costAfterCents) })}</p> : null}
      <ul className={styles.documents} data-bom-cost-documents={ask.documents.length}>
        {ask.documents.map((doc, i) => (
          <li key={`${doc.dealId}-${i}`} data-bom-cost-document={doc.kind}>
            {documentLine(doc)}
          </li>
        ))}
      </ul>
      {refused ? <p className={pageStyles.hint}>{t('deal.bom.costChange.refusedHint')}</p> : null}
      {ask.canOverride ? (
        <div className={styles.override} data-bom-cost-override="true">
          <TextInput chars={60} label={t('deal.bom.costChange.overrideLabel')} value={reason} onChange={setReason} name="bomOverrideReason" disabled={saving} />
          <p className={pageStyles.hint}>{t('deal.bom.costChange.overrideHint')}</p>
        </div>
      ) : null}
    </Modal>
  );
}

/**
 * 🔒 The warning line for the newest costed change after the Quote went out
 * (decision 1466), in the Alert Banner's Warning tone as the Deal's urgent
 * next action draws it: the cost before and after where the viewer sees cost,
 * the revision that stood, who and when, and what the save raised or the
 * reason a holder saved it without the document.
 */
export function BomCostChangeLine({ bomId, change }: { bomId: string; change: BomCostChangeRecord | null | undefined }) {
  const zone = useViewerZone();
  const format = useDateFormat();
  if (!change) return null;
  const standing = [...new Set(change.documents.map((doc) => doc.standing).filter((s): s is string => s !== null && s !== ''))].join(', ');
  const values = { standing, actor: change.actorName, date: formatDate(change.occurredAt, zone, format) };
  const head = change.costBeforeCents !== null && change.costAfterCents !== null
    ? filledNodes(t('deal.bom.costChange.line'), { ...values, before: figure(change.costBeforeCents), after: figure(change.costAfterCents) })
    : fill(t('deal.bom.costChange.lineNoCost'), values);
  const made = change.documents.map((doc) => doc.made).filter((m): m is string => m !== null);
  const tail = change.overrideReason !== null
    ? fill(t('deal.bom.costChange.lineOverride'), { reason: change.overrideReason })
    : made.length > 0 ? fill(t('deal.bom.costChange.lineMade'), { documents: made.join(', ') }) : '';
  return (
    <div className={styles.line} data-bom-cost-change={bomId} data-bom-cost-change-override={change.overrideReason !== null ? 'true' : 'false'}>
      <AlertBanner tone="warning" urging message={tail ? <>{head} {tail}</> : head} />
    </div>
  );
}

/** 🔒 The Quote's page says its bill of materials changed after the revision went out, until a later revision is sent (decision 1466). */
export function QuoteBomChangeLine({ change }: { change: QuoteBomChange | null | undefined }) {
  const zone = useViewerZone();
  const format = useDateFormat();
  if (!change) return null;
  return (
    <div className={styles.line} data-quote-bom-change={change.bomId}>
      <AlertBanner tone="warning" urging message={fill(t('quote.bomChanged'), { bom: change.bomName, standing: change.standing, date: formatDate(change.occurredAt, zone, format) })} />
    </div>
  );
}
